/**
 * Accès en lecture aux fichiers d'un export, quelle que soit sa forme :
 * archive .zip (lue à la demande, sans la charger entièrement en mémoire),
 * dossier extrait ou fichiers sélectionnés.
 */
export interface ArchiveEntry {
  /** Chemin dans l'archive, séparateur « / ». */
  path: string;
  size: number;
  read: () => Promise<Uint8Array>;
}

const EOCD_SIGNATURE = 0x06054b50;
const ZIP64_LOCATOR_SIGNATURE = 0x07064b50;
const ZIP64_EOCD_SIGNATURE = 0x06064b50;
const CENTRAL_SIGNATURE = 0x02014b50;
const LOCAL_SIGNATURE = 0x04034b50;
const MAX_U16 = 0xffff;
const MAX_U32 = 0xffffffff;

async function bytes(blob: Blob, start: number, end: number): Promise<DataView> {
  return new DataView(await blob.slice(start, end).arrayBuffer());
}

const u64 = (view: DataView, offset: number) => Number(view.getBigUint64(offset, true));

async function inflateRaw(data: Uint8Array): Promise<Uint8Array> {
  const stream = new Blob([data as BlobPart])
    .stream()
    .pipeThrough(new DecompressionStream('deflate-raw'));
  return new Uint8Array(await new Response(stream).arrayBuffer());
}

/**
 * Lit le répertoire central d'une archive zip (zip64 compris : les exports Takeout dépassent vite 4 Go).
 * Seules les métadonnées sont lues ici ; le contenu d'un fichier l'est à l'appel de `read()`.
 */
export async function readZip(file: Blob): Promise<ArchiveEntry[]> {
  // L'enregistrement de fin fait 22 octets, suivi d'un commentaire de 64 Ko au plus.
  const tailStart = Math.max(0, file.size - (22 + MAX_U16));
  const tail = await bytes(file, tailStart, file.size);
  let eocd = -1;
  for (let i = tail.byteLength - 22; i >= 0; i--) {
    if (tail.getUint32(i, true) === EOCD_SIGNATURE) {
      eocd = i;
      break;
    }
  }
  if (eocd < 0) throw new Error('Ce fichier n’est pas une archive zip valide');

  let count = tail.getUint16(eocd + 10, true);
  let centralSize = tail.getUint32(eocd + 12, true);
  let centralOffset = tail.getUint32(eocd + 16, true);

  if (count === MAX_U16 || centralSize === MAX_U32 || centralOffset === MAX_U32) {
    const locatorPos = tailStart + eocd - 20;
    const locator = await bytes(file, locatorPos, locatorPos + 20);
    if (locator.getUint32(0, true) !== ZIP64_LOCATOR_SIGNATURE)
      throw new Error('Archive zip64 invalide');
    const recordPos = u64(locator, 8);
    const record = await bytes(file, recordPos, recordPos + 56);
    if (record.getUint32(0, true) !== ZIP64_EOCD_SIGNATURE)
      throw new Error('Archive zip64 invalide');
    count = u64(record, 32);
    centralSize = u64(record, 40);
    centralOffset = u64(record, 48);
  }

  const central = await bytes(file, centralOffset, centralOffset + centralSize);
  const decoder = new TextDecoder();
  const entries: ArchiveEntry[] = [];
  let pos = 0;
  for (let i = 0; i < count; i++) {
    if (central.getUint32(pos, true) !== CENTRAL_SIGNATURE)
      throw new Error('Répertoire de l’archive corrompu');
    const flags = central.getUint16(pos + 8, true);
    const method = central.getUint16(pos + 10, true);
    let compressedSize = central.getUint32(pos + 20, true);
    let size = central.getUint32(pos + 24, true);
    const nameLength = central.getUint16(pos + 28, true);
    const extraLength = central.getUint16(pos + 30, true);
    const commentLength = central.getUint16(pos + 32, true);
    let localOffset = central.getUint32(pos + 42, true);
    const name = decoder.decode(
      new Uint8Array(central.buffer, central.byteOffset + pos + 46, nameLength),
    );

    // Champ « extra » zip64 : seules les valeurs saturées à 0xFFFFFFFF y figurent, dans cet ordre.
    let extra = pos + 46 + nameLength;
    const extraEnd = extra + extraLength;
    while (extra + 4 <= extraEnd) {
      const id = central.getUint16(extra, true);
      const length = central.getUint16(extra + 2, true);
      if (id === 0x0001) {
        let field = extra + 4;
        if (size === MAX_U32) {
          size = u64(central, field);
          field += 8;
        }
        if (compressedSize === MAX_U32) {
          compressedSize = u64(central, field);
          field += 8;
        }
        if (localOffset === MAX_U32) localOffset = u64(central, field);
      }
      extra += 4 + length;
    }
    pos = extraEnd + commentLength;

    if (name.endsWith('/')) continue;
    if (flags & 0x1) throw new Error('Les archives chiffrées ne sont pas prises en charge');

    entries.push({
      path: name,
      size,
      read: async () => {
        const local = await bytes(file, localOffset, localOffset + 30);
        if (local.getUint32(0, true) !== LOCAL_SIGNATURE)
          throw new Error(`Fichier corrompu : ${name}`);
        const dataStart = localOffset + 30 + local.getUint16(26, true) + local.getUint16(28, true);
        const data = new Uint8Array(
          await file.slice(dataStart, dataStart + compressedSize).arrayBuffer(),
        );
        if (method === 0) return data;
        if (method === 8) return inflateRaw(data);
        throw new Error(`Méthode de compression non prise en charge (${method}) : ${name}`);
      },
    });
  }
  return entries;
}

/**
 * Transforme une sélection de fichiers en entrées : les .zip sont ouverts (plusieurs parties
 * d'un même export Takeout se fusionnent naturellement), les autres fichiers sont pris tels quels.
 */
export async function entriesFromFiles(files: File[]): Promise<ArchiveEntry[]> {
  const entries: ArchiveEntry[] = [];
  for (const file of files) {
    if (/\.zip$/i.test(file.name)) {
      entries.push(...(await readZip(file)));
    } else {
      entries.push({
        path: file.webkitRelativePath || file.name,
        size: file.size,
        read: async () => new Uint8Array(await file.arrayBuffer()),
      });
    }
  }
  return entries;
}
